import React, { useCallback, useState } from 'react';
import {
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WidgetsIcon from '@mui/icons-material/Widgets';

import BaseDialog from '../../../dialogs/BaseDialog';
import RoomChat from '../../RoomChat';
import RoomDetail from '../../RoomDetail';
import RoomDetailWithUserDetail from '../../RoomDetailWithUserDetail';
import RoomUserDetail from '../../RoomUserDetail';
import RoomUsers from '../../RoomUsers';
import RoomUsersSP from '../../RoomUsersSP';

const RESPONSIVE_MODE = {
  MOBILE: 'mobile',
  DESKTOP: 'desktop',
};

const MENU_VALUE = {
  ROOM_DETAIL: 'roomDetail',
  ROOM_OVERVIEW: 'roomOverview',
  ROOM_ACHIEVEMENT: 'roomAchievement',
  USERS: 'users',
};

const menuContainerSx = {
  width: '64px',
  padding: '8px',
  boxSizing: 'border-box',
  position: 'sticky',
  top: '88px',
  borderRadius: '18px',
  backgroundColor: 'rgba(255, 255, 255, 0.92)',
  border: '1px solid rgba(148, 163, 184, 0.3)',
  boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
};

const menuButtonGroupSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  alignItems: 'center',
};

const menuButtonSx = {
  width: '48px',
  height: '48px',
  backgroundColor: '#f9fafb',
  border: '1px solid rgba(148, 163, 184, 0.35)',
  borderRadius: '14px',
  color: '#1f2937',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
  transition: 'background-color 0.2s ease, color 0.2s ease, transform 0.2s ease',
  '&:hover': {
    backgroundColor: '#e0edf9',
    color: '#1f4e8c',
    transform: 'translateY(-1px)',
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#e0edf9',
  },
};

const mobileMenuIconSx = {
  position: 'fixed',
  top: '96px',
  right: '12px',
  zIndex: 1190,
  width: '42px',
  height: '42px',
  color: '#111827',
  backgroundColor: 'rgba(255, 255, 255, 0.94)',
  border: '1px solid rgba(17, 24, 39, 0.14)',
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)',
  '&:hover': {
    backgroundColor: '#ffffff',
  },
};

const mobileDrawerSx = {
  width: 280,
};

const dialogBodySx = {
  width: '100%',
  boxSizing: 'border-box',
};

const dialogSizeByResponsiveMode = {
  [RESPONSIVE_MODE.MOBILE]: {
    bodyMaxWidth: '760px',
    maxHeight: 'calc(100dvh - 32px)',
    maxWidth: 'min(96vw, 820px)',
  },
  [RESPONSIVE_MODE.DESKTOP]: {
    bodyMaxWidth: '1040px',
    maxHeight: 'min(88vh, 820px)',
    maxWidth: 'min(92vw, 1120px)',
  },
};

const desktopRoomOverviewDialogSize = {
  bodyMaxWidth: '1360px',
  bodyWidth: 'calc(min(96vw, 1440px) - 40px)',
  maxHeight: 'min(88vh, 820px)',
  maxWidth: 'min(96vw, 1440px)',
};

const mobileMenuItems = [
  { label: 'ルーム詳細情報', value: MENU_VALUE.ROOM_DETAIL },
  { label: 'あなたのルーム内実績', value: MENU_VALUE.ROOM_ACHIEVEMENT },
  { label: '参加者', value: MENU_VALUE.USERS },
  { label: 'チャット', value: 'chat' },
];

const desktopMenuItems = [
  { label: 'ルーム詳細・実績', value: MENU_VALUE.ROOM_OVERVIEW, icon: <InfoOutlinedIcon /> },
  { label: '参加者', value: MENU_VALUE.USERS, icon: <PersonOutlineIcon /> },
  { label: 'チャット', value: 'chat', icon: <ChatBubbleOutlineIcon /> },
];

function MemberRoomMenuContent({
  activeMenu,
  chatProps,
  event,
  eventId,
  masterTags,
  onReady,
  responsiveMode,
  round,
  updateMasterTags,
  user,
}) {
  const isMobile = responsiveMode === RESPONSIVE_MODE.MOBILE;

  switch (activeMenu) {
    case MENU_VALUE.ROOM_DETAIL:
      return (
        <RoomDetail
          user={user}
          event={event}
          round={round}
          masterTags={masterTags}
          updateMasterTags={updateMasterTags}
        />
      );
    case MENU_VALUE.ROOM_ACHIEVEMENT:
      return <RoomUserDetail user={user} />;
    case MENU_VALUE.ROOM_OVERVIEW:
      return (
        <RoomDetailWithUserDetail
          user={user}
          event={event}
          round={round}
          masterTags={masterTags}
          updateMasterTags={updateMasterTags}
        />
      );
    case MENU_VALUE.USERS:
      return isMobile
        ? <RoomUsersSP user={user} event_id={eventId} max={20} onReady={onReady} />
        : <RoomUsers user={user} event_id={eventId} max={20} onReady={onReady} />;
    case 'chat':
      return <RoomChat {...chatProps} />;
    default:
      return null;
  }
}

function MemberRoomSubMenu({ responsiveMode, ...viewProps }) {
  const [activeMenu, setActiveMenu] = useState(null);
  const [menuContentReady, setMenuContentReady] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const unreadChatCount = Number(viewProps.unreadChatCount) || 0;
  const hasUnreadChat = unreadChatCount > 0;
  const chatBadgeContent = unreadChatCount > 9 ? '9+' : unreadChatCount;

  const closeDialog = () => {
    setActiveMenu(null);
    setMenuContentReady(true);
  };
  const closeDrawer = () => setDrawerOpen(false);
  const dialogSize = (
    responsiveMode === RESPONSIVE_MODE.DESKTOP && activeMenu === MENU_VALUE.ROOM_OVERVIEW
      ? desktopRoomOverviewDialogSize
      : dialogSizeByResponsiveMode[responsiveMode]
        ?? dialogSizeByResponsiveMode[RESPONSIVE_MODE.DESKTOP]
  );
  const handleMenuContentReady = useCallback(() => {
    setMenuContentReady(true);
  }, []);
  const openMenuContent = (menuValue) => {
    closeDrawer();
    setMenuContentReady(menuValue !== MENU_VALUE.USERS);
    setActiveMenu(menuValue);
    if (menuValue === 'chat' && typeof viewProps.onOpenChat === 'function') {
      viewProps.onOpenChat();
    }
  };

  const dialogContent = (
    <BaseDialog
      isVisible={Boolean(activeMenu) && menuContentReady}
      onClose={closeDialog}
      maxWidth={dialogSize.maxWidth}
      maxHeight={dialogSize.maxHeight}
    >
      <Box
        sx={{
          ...dialogBodySx,
          maxWidth: dialogSize.bodyMaxWidth,
          ...(dialogSize.bodyWidth ? { width: dialogSize.bodyWidth } : {}),
        }}
      >
        <MemberRoomMenuContent
          activeMenu={activeMenu}
          onReady={handleMenuContentReady}
          responsiveMode={responsiveMode}
          {...viewProps}
        />
      </Box>
    </BaseDialog>
  );

  if (responsiveMode === RESPONSIVE_MODE.MOBILE) {
    return (
      <>
        <IconButton
          aria-label="room menu"
          onClick={() => setDrawerOpen(true)}
          sx={{
            ...mobileMenuIconSx,
            ...(hasUnreadChat ? {
              borderColor: 'rgba(211, 47, 47, 0.42)',
              boxShadow: '0 0 0 3px rgba(211, 47, 47, 0.12), 0 6px 18px rgba(0, 0, 0, 0.18)',
            } : {}),
          }}
        >
          <Badge
            badgeContent={chatBadgeContent}
            color="error"
            invisible={!hasUnreadChat}
            overlap="circular"
          >
            <WidgetsIcon />
          </Badge>
        </IconButton>
        <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
          <Box sx={mobileDrawerSx} role="presentation">
            <Box sx={{ padding: '16px 16px 8px' }}>
              <Typography variant="subtitle2" color="text.secondary">
                ルームメニュー
              </Typography>
            </Box>
            <Divider />
            <List>
              {mobileMenuItems.map((item) => (
                <ListItem key={item.value} disablePadding>
                  <ListItemButton onClick={() => openMenuContent(item.value)}>
                    <ListItemText primary={item.label} />
                    {item.value === 'chat' && hasUnreadChat ? (
                      <Box
                        component="span"
                        sx={{
                          minWidth: 22,
                          height: 20,
                          px: 0.75,
                          borderRadius: '999px',
                          backgroundColor: 'error.main',
                          color: '#fff',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.72rem',
                          fontWeight: 800,
                          lineHeight: 1,
                        }}
                      >
                        {chatBadgeContent}
                      </Box>
                    ) : null}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>
        {dialogContent}
      </>
    );
  }

  return (
    <Box sx={menuContainerSx} aria-label="ルームメニュー">
      <Box sx={menuButtonGroupSx}>
        {desktopMenuItems.map((item) => (
          <Tooltip
            key={item.value}
            title={item.label}
            placement="left"
            arrow
          >
            <IconButton
              aria-label={item.label}
              onClick={() => openMenuContent(item.value)}
              sx={menuButtonSx}
            >
              {item.value === 'chat' ? (
                <Badge
                  badgeContent={chatBadgeContent}
                  color="error"
                  invisible={!hasUnreadChat}
                  overlap="circular"
                >
                  {item.icon}
                </Badge>
              ) : item.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
      {dialogContent}
    </Box>
  );
}

export default MemberRoomSubMenu;
